import { formatDistanceToNow } from 'date-fns';
import { ArrowLeft, History, RotateCcw, Save } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { isCrashingOutEnabledForUser } from '@/lib/featureFlags';
import type { AgentflowConfigVersion } from '@/services/api/agentflowConfigService';
import {
  useGetAgentflowConfigQuery,
  useGetAgentflowConfigVersionsQuery,
  useUpdateAgentflowConfigMutation,
} from '@/services/api/agentflowConfigService';
import { selectUser } from '@/state/selectors/root-selectors';

const MAX_SYSTEM_PROMPT_LENGTH = 50_000;

// ---------------------------------------------------------------------------
// Version History Item
// ---------------------------------------------------------------------------

const VersionItem = ({
  version,
  isActive,
  isSelected,
  onSelect,
  onRestore,
}: {
  version: AgentflowConfigVersion;
  isActive: boolean;
  isSelected: boolean;
  onSelect: (v: AgentflowConfigVersion) => void;
  onRestore: (v: AgentflowConfigVersion) => void;
}) => {
  const prompt = version.system_prompt;

  return (
    <button
      type="button"
      onClick={() => onSelect(version)}
      className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
        isSelected
          ? 'border-indigo-300 bg-indigo-50'
          : 'border-zinc-200 bg-white hover:bg-zinc-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-zinc-900">v{version.version_number}</span>
          {isActive && (
            <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
              Active
            </Badge>
          )}
        </div>
        <span className="text-xs text-zinc-500">
          {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
        </span>
      </div>
      <p className="mt-1 text-xs text-zinc-500 line-clamp-2">
        {prompt ? prompt.slice(0, 120) + (prompt.length > 120 ? '...' : '') : 'Default prompt'}
      </p>
      {isSelected && (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            variant="outline"
            className="h-7 gap-1 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onRestore(version);
            }}
          >
            <RotateCcw className="h-3 w-3" />
            Restore
          </Button>
        </div>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const AgentflowConfigPage = () => {
  const user = useSelector(selectUser);
  const { data: config, isLoading: configLoading } = useGetAgentflowConfigQuery();
  const { data: versions = [], isLoading: versionsLoading } =
    useGetAgentflowConfigVersionsQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdateAgentflowConfigMutation();

  const [draft, setDraft] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AgentflowConfigVersion | null>(null);

  const hasCustomOverride = config?.selected_version_id != null;
  const effectivePrompt = config?.crashing_out.system_prompt ?? config?.default_system_prompt ?? '';

  // Sync draft from server on first load
  useEffect(() => {
    if (config && !initialized) {
      setDraft(effectivePrompt);
      setInitialized(true);
    }
  }, [config, initialized, effectivePrompt]);

  const isDirty = initialized && draft !== effectivePrompt;

  const handleSave = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      toast.error('System prompt cannot be empty');
      return;
    }
    try {
      await updateConfig({
        crashing_out: { system_prompt: trimmed },
      }).unwrap();
      toast.success('System prompt saved');
      setSelectedVersion(null);
    } catch {
      toast.error('Failed to save system prompt');
    }
  }, [draft, updateConfig]);

  const handleResetToDefault = useCallback(async () => {
    try {
      await updateConfig({
        crashing_out: { system_prompt: null },
      }).unwrap();
      setDraft(config?.default_system_prompt ?? '');
      setSelectedVersion(null);
      toast.success('Reset to default prompt');
    } catch {
      toast.error('Failed to reset prompt');
    }
  }, [updateConfig, config?.default_system_prompt]);

  const handleRestore = useCallback(
    async (version: AgentflowConfigVersion) => {
      const prompt = version.system_prompt;
      try {
        await updateConfig({
          crashing_out: { system_prompt: prompt ?? null },
        }).unwrap();
        setDraft(prompt ?? config?.default_system_prompt ?? '');
        setSelectedVersion(null);
        toast.success(`Restored v${version.version_number}`);
      } catch {
        toast.error('Failed to restore version');
      }
    },
    [updateConfig, config?.default_system_prompt],
  );

  if (!isCrashingOutEnabledForUser(user?.email)) {
    return <Navigate to="/matters" replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/quant-mode"
          className="inline-flex items-center gap-1 text-sm font-medium text-zinc-500 hover:text-zinc-900 mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Quant Mode
        </Link>
        <h1 className="text-lg font-semibold text-zinc-900">Crashing Out Config</h1>
        <p className="text-sm text-zinc-600">
          Configure the system prompt used by all Crashing Out workflow runs.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Editor */}
        <div className="flex-1 space-y-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-zinc-700">
                System Prompt{hasCustomOverride ? ' (custom override)' : ' (default)'}
              </span>
              <span className="text-xs text-zinc-500">
                {draft.length.toLocaleString()} / {MAX_SYSTEM_PROMPT_LENGTH.toLocaleString()} chars
              </span>
            </div>
            <Textarea
              aria-label="System prompt override"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Using default prompt — enter a custom prompt to override."
              className="min-h-[400px] font-mono text-sm"
              maxLength={MAX_SYSTEM_PROMPT_LENGTH}
              disabled={configLoading}
            />

            {config?.modified_at && (
              <p className="mt-2 text-xs text-zinc-500">
                Last modified{' '}
                {formatDistanceToNow(new Date(config.modified_at), { addSuffix: true })}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!isDirty || isSaving}
              onClick={handleSave}
            >
              <Save className="h-3.5 w-3.5" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={isSaving || !hasCustomOverride}
              onClick={handleResetToDefault}
            >
              Reset to default
            </Button>
            {isDirty && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Unsaved changes
              </Badge>
            )}
          </div>
        </div>

        {/* Version History Sidebar */}
        <div className="w-full lg:w-80 shrink-0">
          <div className="rounded-xl border border-zinc-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-zinc-500" />
              <h2 className="text-sm font-medium text-zinc-700">Version History</h2>
            </div>

            {versionsLoading ? (
              <p className="text-xs text-zinc-500">Loading...</p>
            ) : versions.length === 0 ? (
              <p className="text-xs text-zinc-500">No versions yet. Save a prompt to create one.</p>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {versions.map((v) => (
                  <VersionItem
                    key={v.id}
                    version={v}
                    isActive={config?.selected_version_id === v.id}
                    isSelected={selectedVersion?.id === v.id}
                    onSelect={setSelectedVersion}
                    onRestore={handleRestore}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
